/**
 * The `permanent` filter set for `/tina/refresh-review`.
 *
 * Every entry is a real `GET /refresh-jobs` param — `run_id`, `status`, `refresh_outcome`,
 * `mode`, `site`, `admin_event_id` — so the whole page is server-side: the rows, the `total` the
 * pagination footer reads, and the narrowing. That is the reason this page reads `v_refresh_jobs`
 * rather than `GET /events`, which can name the refresh class (`content_type=music_event_refresh`)
 * and the reason (`hitl_reason=refresh_review`) but returns mode / proposal counts / page-gone
 * only as raw `metadata` jsonb.
 *
 * **`status=hitl` is the queue.** `GET /refresh-jobs` has no `hitl_reason` param, and it does not
 * need one: `refresh_review` is the only reason the `music_event_refresh` pipeline can park on —
 * `refresh_apply` is its only HITL-raising stage, and `_REFRESH_REASONS` has exactly one member —
 * so on a refresh job `status=hitl` and `hitl_reason=refresh_review` name the same rows. If the
 * pipeline ever grows a second reason this filter widens silently, which is the safe direction
 * for a curator queue: an unlisted park is invisible work.
 *
 * Same always-emit convention as `buildRefreshJobsFilters` and `buildItemsFilters`: a
 * user-controlled field is present on every render carrying `value: undefined` when cleared,
 * never omitted. Refine builds the query as `unionFilters(permanent, internalState)` and drops
 * undefined entries *after* the dedupe, so a present-but-undefined entry shadows a stale internal
 * copy while an omitted one lets it survive — which is how a table gets stuck on a filter the
 * curator cleared.
 */
import type { CrudFilters } from "@refinedev/core";

import type { RefreshMode } from "@/types/tina";

export interface RefreshReviewFilterState {
  /** The toolbar's mode select; `null` is "any", which sends no `mode` at all. */
  mode: RefreshMode | null;
}

export const buildRefreshReviewFilters = ({
  mode,
}: RefreshReviewFilterState): CrudFilters =>
  [
    // Pinned, not user-controlled: this page IS the parked slice. `permanent`
    // wins the union, so nothing a column filter sets can widen it.
    { field: "status", operator: "eq", value: "hitl" },
    { field: "mode", operator: "eq", value: mode ?? undefined },
  ] as CrudFilters;
